/* eslint-disable import/extensions,no-underscore-dangle */
/* globals global, __DEV__, PROD_ENV, SENTRY_DSN, GOOGLE_ANALYTICS_ID, FACEBOOK_PIXEL_ID, GOOGLE_OPTIMIZE_ID, ELASTIC_APM_SERVICE_NAME, ELASTIC_APM_SECRET_TOKEN, ELASTIC_APM_SERVER_URL, ELASTIC_APM_ENVIRONMENT, POSTHOG_KEY, POSTHOG_HOST */
import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
import { PersistGate } from 'redux-persist/integration/react'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import Promise from 'promise'
import ReactGA from 'react-ga4'
import posthog from 'posthog-js'
import ReactPixel from 'react-facebook-pixel'
import React from 'react'
import * as Sentry from '@sentry/browser'
import { init as initApm } from '@elastic/apm-rum'
import routes from './routes.jsx'
import store, { persistor } from './redux/store'
import { setStore, setRouter } from './utils/navigation'
import {
  IGNORED_ERROR_MESSAGES,
  dropIgnoredExceptions,
  scrubExceptionPii
} from './utils/ignoredErrors'
import { setCheckoutErrorContext } from './utils/checkoutErrors'
import { registerRelease } from './utils/posthog'

import './index.css'

// Initialize navigation utilities with the redux store (for transition compatibility)
setStore(store)
// SPEC-077: checkout_error events read the selected plan from the store
setCheckoutErrorContext(store.getState)

// Create the RR6 browser router with future flags for v7 compatibility
const router = createBrowserRouter(routes, {
  future: {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  }
})

// Set the router in navigation utilities for RR6 navigation
setRouter(router)

initApm({
  serviceName: ELASTIC_APM_SERVICE_NAME,
  serverUrl: ELASTIC_APM_SERVER_URL,
  environment: ELASTIC_APM_ENVIRONMENT
})

require('./favicon.ico') // Tell webpack to load favicon.ico

// To add to global
if (!global.Promise) global.Promise = Promise

if (PROD_ENV) {
  // Facebook Pixel initialize
  ReactPixel.init(FACEBOOK_PIXEL_ID)

  // Google Analytics 4 conf
  ReactGA.initialize(GOOGLE_ANALYTICS_ID)
  // Note: Google Optimize was deprecated in Sept 2023
  // ReactGA.ga('require', GOOGLE_OPTIMIZE_ID) - removed as not supported in GA4

  // PostHog product analytics. `defaults` enables the current recommended
  // config (history-based pageview capture for SPA route changes, autocapture).
  if (POSTHOG_KEY && POSTHOG_KEY !== 'phc_REPLACE_ME')
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      // When POSTHOG_HOST is switched to a same-origin reverse-proxy path (see
      // .env) to dodge ad blockers, ui_host keeps the toolbar / "open in
      // PostHog" links pointing at PostHog itself.
      ui_host: 'https://us.posthog.com',
      defaults: '2025-05-24',
      person_profiles: 'identified_only',
      // Session replay with privacy-safe masking: every input value and any
      // element tagged [data-ph-mask] is redacted. Cross-origin iframes (e.g.
      // Braintree hosted payment fields) are never recorded. Requires "Record
      // user sessions" to be enabled in the PostHog project settings.
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '[data-ph-mask]'
      },
      // SPEC-070: JS errors land next to the customer's replay. Unhandled
      // errors and promise rejections only — console.error is noise, not a
      // failure. posthog-js rate-limits repeated exceptions on its own.
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: false
      },
      // drop extension noise first, then scrub what is left (SPEC-077 FR-6)
      before_send: [dropIgnoredExceptions, scrubExceptionPii],
      // SPEC-075: every event carries its release (see registerRelease)
      loaded: registerRelease
    })
}

// The npm build doesn't put the instance on window; the PostHog toolbar and
// feature-flag overrides from the console on dev (SPEC-102 AC-1:
// posthog.featureFlags.overrideFeatureFlags({ flags: { ... } })) need it.
// Harmless when PostHog isn't configured: every method no-ops.
window.posthog = posthog

// Page view tracking - now handled by RR6 router events
// Note: For RR6, page tracking should be handled via useEffect in App component
// or via router.subscribe() if needed

// Setup Sentry
if (!__DEV__)
  Sentry.init({
    dsn: SENTRY_DSN,
    allowUrls: [/stanwinstonschool\.com/, /dev\.swsca\.talpor.com/],
    ignoreErrors: IGNORED_ERROR_MESSAGES,
    denyUrls: [/extensions\//i, /^chrome:\/\//i]
  })

// ------------------------------
const root = createRoot(document.getElementById('app'))
root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </PersistGate>
  </Provider>
)
